import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTenantContext } from '../../../context/TenantContext';
import { useToast } from '../../../hooks/useToast';
import { supabase } from '../../../lib/supabase';
import { applyDomainFilterToQuery, getDomainFilterParams } from '../../../hooks/useDomainQuery';
import { AdminLayout } from '../../../components/layout/AdminLayout';
import { 
  toggleReviewPublicationStatus, 
  deleteCourseReview,
  CourseReview
} from '../../../services/elearningReviewService';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useCheckPermissions } from '../../../hooks/useCheckPermissions';
import { 
  BookOpen, 
  Search, 
  Filter, 
  Trash2, 
  Eye, 
  EyeOff,
  Star,
  MoreHorizontal,
  MessageSquare,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  XIcon,
  Clock
} from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription, DialogClose } from '../../../components/ui/dialog';
import { Avatar, AvatarFallback, AvatarImage } from '../../../components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/select';
import { Alert, AlertDescription } from '../../../components/ui/alert';
import { cn } from '../../../lib/utils';

// Type pour les cours
type Course = {
  id: string;
  title: string;
  slug: string;
  thumbnail_url: string | null;
};

const AdminCourseReviewsPage: React.FC = () => {
  useCheckPermissions(['admin']);
  const navigate = useNavigate();
  const { tenant } = useTenantContext();
  const { toast } = useToast();

  const [reviews, setReviews] = useState<CourseReview[]>([]);
  const [courses, setCourses] = useState<Course[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCourse, setSelectedCourse] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [sortBy, setSortBy] = useState<'created_at' | 'rating'>('created_at');
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedReview, setSelectedReview] = useState<CourseReview | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);
  const [confirmDialogProps, setConfirmDialogProps] = useState<{
    title: string;
    message: string;
    confirmLabel: string;
    cancelLabel: string;
    onConfirm: () => void;
  }>({ 
    title: '', 
    message: '', 
    confirmLabel: '', 
    cancelLabel: '', 
    onConfirm: () => {} 
  });

  // Charger les cours et les avis
  useEffect(() => {
    const { tenantId } = getDomainFilterParams();
    if (tenantId) {
      fetchCourses();
      fetchReviews();
    }
  }, []);

  // Récupérer tous les cours
  const fetchCourses = async () => {
    try {
      let coursesQuery = supabase
        .from('elearn_courses')
        .select('id, title, slug, thumbnail_url');
      
      coursesQuery = applyDomainFilterToQuery(coursesQuery);
      const { data, error } = await coursesQuery
        .order('title', { ascending: true });

      if (error) throw error;
      setCourses(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des cours:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les cours.',
        variant: 'destructive'
      });
    }
  };

  // Récupérer tous les avis
  const fetchReviews = async () => {
    setIsLoading(true);
    try {
      let reviewsQuery = supabase
        .from('elearn_course_reviews')
        .select(`
          *,
          student:student_id(id, name:full_name, avatar_url),
          course:course_id(id, title, slug)
        `);
      
      reviewsQuery = applyDomainFilterToQuery(reviewsQuery);
      const { data, error } = await reviewsQuery
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReviews(data || []);
    } catch (error) {
      console.error('Erreur lors de la récupération des avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les avis.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Gérer la publication/dépublication d'un avis
  const handleTogglePublishStatus = async (review: CourseReview) => {
    try {
      const { tenantId } = getDomainFilterParams();
      const newStatus = !review.is_published;
      const success = await toggleReviewPublicationStatus(tenantId || '', review.id, newStatus);
      
      if (success) {
        toast({
          title: newStatus ? 'Avis publié' : 'Avis dépublié',
          description: newStatus 
            ? 'L\'avis est maintenant visible par tous les utilisateurs.' 
            : 'L\'avis n\'est plus visible par les utilisateurs.',
          variant: 'default'
        });
        
        // Mettre à jour l'état local
        setReviews(reviews.map(r => 
          r.id === review.id ? { ...r, is_published: newStatus } : r
        ));
      } else {
        throw new Error('Échec de la mise à jour du statut');
      }
    } catch (error) {
      console.error('Erreur lors de la modification du statut de publication:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de modifier le statut de publication de cet avis.',
        variant: 'destructive'
      });
    }
  };

  // Confirmer la suppression d'un avis
  const confirmDeleteReview = (review: CourseReview) => {
    setConfirmDialogProps({
      title: 'Supprimer cet avis ?',
      message: `Êtes-vous sûr de vouloir supprimer définitivement cet avis de ${review.student?.name || 'l\'étudiant'} ? Cette action ne peut pas être annulée.`,
      confirmLabel: 'Supprimer',
      cancelLabel: 'Annuler',
      onConfirm: () => handleDeleteReview(review.id)
    });
    setIsConfirmDialogOpen(true);
  };

  // Supprimer un avis
  const handleDeleteReview = async (reviewId: string) => {
    try {
      const { tenantId } = getDomainFilterParams();
      const success = await deleteCourseReview(tenantId || '', reviewId);
      
      if (success) {
        toast({
          title: 'Avis supprimé',
          description: 'L\'avis a été supprimé avec succès.',
          variant: 'default'
        });
        
        // Mettre à jour l'état local
        setReviews(reviews.filter(r => r.id !== reviewId));
        setIsConfirmDialogOpen(false);
      } else {
        throw new Error('Échec de la suppression');
      }
    } catch (error) {
      console.error('Erreur lors de la suppression de l\'avis:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer cet avis.',
        variant: 'destructive'
      });
      setIsConfirmDialogOpen(false);
    }
  };

  // Afficher les détails d'un avis
  const showReviewDetails = (review: CourseReview) => {
    setSelectedReview(review);
    setIsDetailDialogOpen(true);
  };

  // Filtrer les avis
  const filteredReviews = reviews
    .filter(review => {
      // Filtre par cours
      if (selectedCourse !== 'all' && review.course_id !== selectedCourse) {
        return false;
      }
      
      // Filtre par statut
      if (selectedStatus === 'published' && !review.is_published) {
        return false;
      }
      if (selectedStatus === 'pending' && review.is_published) {
        return false;
      }
      
      // Filtre par recherche (texte de l'avis ou nom de l'étudiant)
      const searchLower = searchQuery.toLowerCase();
      if (searchQuery && !(
        (review.review_text && review.review_text.toLowerCase().includes(searchLower)) ||
        (review.student?.name && review.student.name.toLowerCase().includes(searchLower)) ||
        (review.course?.title && review.course.title.toLowerCase().includes(searchLower))
      )) {
        return false;
      }
      
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'rating') {
        return b.rating - a.rating;
      }
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    });

  return (
    <AdminLayout>
      <div className="min-h-screen bg-gradient-to-b from-gray-900 to-black text-white pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* En-tête */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
            <div>
              <motion.h1 
                className="text-3xl font-bold text-white mb-2 flex items-center"
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, type: "spring" }}
              >
                <motion.div
                  initial={{ rotate: -10, scale: 0.9 }}
                  animate={{ rotate: 0, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.2 }}
                >
                  <MessageSquare className="mr-3 h-8 w-8 text-yellow-500" />
                </motion.div>
                Gestion des avis de cours
              </motion.h1>
              <p className="text-gray-400">
                Gérez et modérez les avis laissés par les étudiants sur vos cours.
              </p>
            </div>
            <div className="mt-4 md:mt-0">
              <motion.div
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Button 
                  className="bg-yellow-500 hover:bg-yellow-600 text-black font-medium"
                  onClick={() => fetchReviews()}
                >
                  <Clock className="mr-2 h-4 w-4" /> Rafraîchir
                </Button>
              </motion.div>
            </div>
          </div>

          {/* Filtres */}
          <div className="mb-8">
            <div className="bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 p-6 shadow-lg">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center">
                <Filter className="mr-2 h-5 w-5 text-yellow-500" /> Filtres et recherche
              </h2>
              <div className="flex flex-col md:flex-row gap-4">
                <div className="w-full md:w-1/3">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Recherche</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                      type="search"
                      placeholder="Rechercher un avis..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder-gray-400 focus:border-yellow-500/50 focus:ring-yellow-500/50"
                    />
                  </div>
                </div>
                
                <div className="w-full md:w-48">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Cours</label>
                  <div className="relative">
                    <select
                      value={selectedCourse}
                      onChange={(e) => setSelectedCourse(e.target.value)}
                      className="w-full appearance-none rounded-md bg-white/5 border border-white/10 text-white py-2 pl-3 pr-8 focus:border-yellow-500/50 focus:ring-yellow-500/50 cursor-pointer"
                    >
                      <option value="all" className="bg-gray-800 text-white">Tous les cours</option>
                      {courses.map((course) => (
                        <option key={course.id} value={course.id} className="bg-gray-800 text-white">
                          {course.title}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-48">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Statut</label>
                  <div className="relative">
                    <select
                      value={selectedStatus}
                      onChange={(e) => setSelectedStatus(e.target.value)}
                      className="w-full appearance-none rounded-md bg-white/5 border border-white/10 text-white py-2 pl-3 pr-8 focus:border-yellow-500/50 focus:ring-yellow-500/50 cursor-pointer"
                    >
                      <option value="all" className="bg-gray-800 text-white">Tous les statuts</option>
                      <option value="published" className="bg-gray-800 text-white">Publiés</option>
                      <option value="pending" className="bg-gray-800 text-white">En attente</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                
                <div className="w-full md:w-48">
                  <label className="block text-sm font-medium text-gray-300 mb-1">Trier par</label>
                  <div className="relative">
                    <select
                      value={sortBy}
                      onChange={(e) => setSortBy(e.target.value as 'created_at' | 'rating')}
                      className="w-full appearance-none rounded-md bg-white/5 border border-white/10 text-white py-2 pl-3 pr-8 focus:border-yellow-500/50 focus:ring-yellow-500/50 cursor-pointer"
                    >
                      <option value="created_at" className="bg-gray-800 text-white">Date (récent d'abord)</option>
                      <option value="rating" className="bg-gray-800 text-white">Note (élevée d'abord)</option>
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                      <ChevronDown className="h-4 w-4 text-gray-400" />
                    </div>
                  </div>
                </div>
                
                <div className="flex items-end">
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setSearchQuery('');
                      setSelectedCourse('all');
                      setSelectedStatus('all');
                      setSortBy('created_at');
                    }}
                    className="border-white/10 text-white hover:bg-white/5 h-10"
                  >
                    <XIcon className="mr-2 h-4 w-4" /> Réinitialiser
                  </Button>
                </div>
              </div>
            </div>
          </div>

          {/* Liste des avis */}
          <div className="bg-black/20 backdrop-blur-sm rounded-xl border border-white/10 p-6 shadow-lg">
            <div className="flex justify-between items-center mb-6">
              <motion.h2 
                className="text-xl font-semibold text-white flex items-center"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <MessageSquare className="mr-2 h-5 w-5 text-yellow-500" /> 
                </motion.div>
                Liste des avis
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 500 }}
                >
                  <Badge className="ml-3 bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">
                    {filteredReviews.length}
                  </Badge>
                </motion.div>
              </motion.h2>
            </div>

            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <Clock className="animate-spin h-8 w-8 text-yellow-500 mr-3" />
                <span className="text-white text-lg">Chargement...</span>
              </div>
            ) : filteredReviews.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-20">
                <MessageSquare className="h-16 w-16 text-gray-600 mb-4" />
                <h3 className="text-xl font-medium text-white mb-2">Aucun avis trouvé</h3>
                <p className="text-gray-400">
                  {searchQuery || selectedCourse !== 'all' || selectedStatus !== 'all'
                    ? "Aucun avis ne correspond à vos critères de recherche."
                    : "Aucun avis n'a encore été laissé sur vos cours."}
                </p>
                <Button 
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedCourse('all');
                    setSelectedStatus('all');
                  }} 
                  className="mt-4 bg-yellow-500 hover:bg-yellow-600 text-black"
                >
                  Réinitialiser les filtres
                </Button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Étudiant</th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Cours</th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Note</th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Statut</th>
                      <th className="py-3 px-4 text-left text-sm font-medium text-gray-300">Date</th>
                      <th className="py-3 px-4 text-right text-sm font-medium text-gray-300">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredReviews.map((review, index) => (
                      <motion.tr 
                        key={review.id} 
                        className={`border-b border-white/5 hover:bg-white/10 ${index % 2 === 0 ? 'bg-white/5' : 'bg-transparent'}`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        whileHover={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', scale: 1.01 }}
                      >
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-3">
                            <Avatar className="h-10 w-10 border border-white/10">
                              {review.student?.avatar_url ? (
                                <AvatarImage src={review.student.avatar_url} alt={review.student?.name || ''} />
                              ) : (
                                <AvatarFallback className="bg-yellow-500/20 text-yellow-500">
                                  {(review.student?.name || 'U').charAt(0)}
                                </AvatarFallback>
                              )}
                            </Avatar>
                            <div>
                              <div className="font-medium text-white">{review.student?.name || 'Utilisateur inconnu'}</div>
                              <div className="text-xs text-gray-400">{review.student?.id || ''}</div>
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="max-w-[200px]">
                            <div className="font-medium text-white truncate">{review.course?.title || 'Cours inconnu'}</div>
                            <div className="text-xs text-gray-400 truncate">{review.course?.slug || ''}</div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center">
                            <span className="font-medium text-white mr-2">{review.rating}</span>
                            <div className="flex">
                              {Array.from({ length: 5 }).map((_, i) => (
                                <Star 
                                  key={i} 
                                  size={14} 
                                  className={i < review.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-600"}
                                />
                              ))}
                            </div>
                          </div>
                        </td>
                        <td className="py-3 px-4">
                          {review.is_published ? (
                            <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                              <CheckCircle className="mr-1 h-3 w-3" /> Publié
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                              <AlertCircle className="mr-1 h-3 w-3" /> En attente
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-gray-300">
                          {format(new Date(review.created_at), 'dd MMM yyyy', { locale: fr })}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end space-x-2">
                            <motion.div
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 px-2 text-gray-300 hover:text-white hover:bg-white/10"
                                onClick={() => showReviewDetails(review)}
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </motion.div>
                            <motion.div
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className={cn(
                                  "h-8 px-2 hover:bg-white/10",
                                  review.is_published ? "text-amber-400 hover:text-amber-300" : "text-green-400 hover:text-green-300"
                                )}
                                onClick={() => handleTogglePublishStatus(review)}
                              >
                                {review.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                              </Button>
                            </motion.div>
                            <motion.div
                              whileHover={{ scale: 1.15 }}
                              whileTap={{ scale: 0.9 }}
                              transition={{ type: "spring", stiffness: 400, damping: 17 }}
                            >
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 px-2 text-red-400 hover:text-red-300 hover:bg-white/10"
                                onClick={() => confirmDeleteReview(review)}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </motion.div>
                          </div>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Boîte de dialogue de confirmation */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="bg-gray-900 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center">
              <Trash2 className="mr-2 h-5 w-5 text-red-400" />
              Confirmer la suppression
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Cette action est irréversible et supprimera définitivement l'avis.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 border-y border-white/10 my-2">
            <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
              <p className="text-white">Êtes-vous sûr de vouloir supprimer cet avis ? Cette action ne peut pas être annulée.</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setIsConfirmDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Annuler
            </Button>
            <Button 
              onClick={confirmDialogProps.onConfirm}
              className="bg-red-500 hover:bg-red-600 text-white"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Boîte de dialogue des détails de l'avis */}
      <Dialog open={isDetailDialogOpen} onOpenChange={setIsDetailDialogOpen}>
        <DialogContent className="max-w-2xl bg-gray-900 border border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-semibold text-white flex items-center">
              <MessageSquare className="mr-2 h-5 w-5 text-yellow-500" />
              Détails de l'avis
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Consultez et modérez l'avis laissé par l'étudiant.
            </DialogDescription>
          </DialogHeader>
          {selectedReview && (
            <div className="space-y-4 py-2">
              <div className="flex items-center gap-4 p-4 bg-white/5 rounded-lg border border-white/10">
                <Avatar className="h-12 w-12 border border-white/10">
                  {selectedReview.student?.avatar_url ? (
                    <AvatarImage src={selectedReview.student.avatar_url} alt={selectedReview.student?.name || ''} />
                  ) : (
                    <AvatarFallback className="bg-yellow-500/20 text-yellow-500">
                      {(selectedReview.student?.name || 'U').charAt(0)}
                    </AvatarFallback>
                  )}
                </Avatar>
                <div>
                  <h3 className="font-medium text-white text-lg">{selectedReview.student?.name || 'Utilisateur inconnu'}</h3>
                  <p className="text-sm text-gray-400 flex items-center">
                    <Clock className="mr-1 h-3 w-3" />
                    {format(new Date(selectedReview.created_at), 'dd MMMM yyyy à HH:mm', { locale: fr })}
                  </p>
                </div>
                <div className="ml-auto">
                  {selectedReview.is_published ? (
                    <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                      <CheckCircle className="mr-1 h-3 w-3" /> Publié
                    </Badge>
                  ) : (
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                      <AlertCircle className="mr-1 h-3 w-3" /> En attente
                    </Badge>
                  )}
                </div>
              </div>
              
              <div className="p-5 bg-white/5 rounded-lg border border-white/10">
                <div className="flex items-center gap-2 mb-4 pb-3 border-b border-white/10">
                  <BookOpen className="h-5 w-5 text-yellow-500" />
                  <span className="font-medium text-white">{selectedReview.course?.title || 'Cours inconnu'}</span>
                </div>
                
                <div className="mb-6">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Note attribuée</h4>
                  <div className="flex items-center">
                    <div className="flex">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star 
                          key={i} 
                          size={20} 
                          className={i < selectedReview.rating ? "fill-yellow-400 text-yellow-400" : "text-gray-600"}
                        />
                      ))}
                    </div>
                    <span className="ml-3 text-lg font-bold text-white">{selectedReview.rating}/5</span>
                  </div>
                </div>
                
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-400 mb-2">Commentaire</h4>
                  <div className="p-4 bg-black/30 rounded-lg border border-white/5">
                    <p className="text-white whitespace-pre-wrap">{selectedReview.review_text}</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg flex items-center">
                <AlertCircle className="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
                <p className="text-sm text-blue-300">
                  Cet avis a été créé le {format(new Date(selectedReview.created_at), 'dd MMMM yyyy', { locale: fr })}.
                  {selectedReview.is_published 
                    ? " Il est actuellement visible par tous les utilisateurs." 
                    : " Il n'est pas encore visible par les utilisateurs."}
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2 pt-2 border-t border-white/10 mt-2">
            <Button 
              variant="outline" 
              onClick={() => setIsDetailDialogOpen(false)}
              className="border-white/10 text-white hover:bg-white/5"
            >
              Fermer
            </Button>
            {selectedReview && (
              <>
                <Button 
                  className={selectedReview.is_published 
                    ? "bg-amber-500 hover:bg-amber-600 text-black" 
                    : "bg-green-500 hover:bg-green-600 text-black"}
                  onClick={() => {
                    handleTogglePublishStatus(selectedReview);
                    setIsDetailDialogOpen(false);
                  }}
                >
                  {selectedReview.is_published ? (
                    <>
                      <EyeOff className="mr-2 h-4 w-4" />
                      Dépublier
                    </>
                  ) : (
                    <>
                      <Eye className="mr-2 h-4 w-4" />
                      Publier
                    </>
                  )}
                </Button>
                <Button 
                  className="bg-red-500 hover:bg-red-600 text-white"
                  onClick={() => {
                    setIsDetailDialogOpen(false);
                    confirmDeleteReview(selectedReview);
                  }}
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminCourseReviewsPage;
